import React from "react";
import { Link } from "react-router-dom";
import { CREST_URL } from "../components/SiteLayout";
import SectionHeader from "../components/SectionHeader";
import { GraduationCap, Users, HandHeart, ArrowRight } from "lucide-react";

const PILLARS = [
  {
    icon: Users,
    title: "Brotherhood",
    text: "A disciplined bond among first-year men who hold one another accountable, encourage one another, and move together through their Howard journey.",
  },
  {
    icon: GraduationCap,
    title: "Scholarship",
    text: "Academic excellence is non-negotiable. We cultivate the study habits, mentorship, and intellectual rigor that define lifelong success.",
  },
  {
    icon: HandHeart,
    title: "Service",
    text: "We serve Howard, the District of Columbia, and the broader Black community &mdash; turning what we have into something that lifts others.",
  },
];

const HomePage = () => {
  return (
    <div>
      {/* HERO */}
      <section className="hero-bg grain-overlay relative overflow-hidden" data-testid="hero-section">
        <div className="relative max-w-7xl mx-auto px-5 py-28 lg:py-40 text-center">
          <img
            src={CREST_URL}
            alt="GODSC Crest"
            className="mx-auto h-40 sm:h-52 w-auto drop-shadow-[0_6px_20px_rgba(0,0,0,0.4)] fade-up"
            data-testid="hero-crest"
          />
          <div className="overline text-[#D4AF37] mt-8 fade-up delay-100">
            The Gentlemen of Drew Social Club, Inc.
          </div>
          <h1 className="font-serif-display text-white font-semibold text-5xl sm:text-6xl lg:text-7xl mt-4 leading-[1.02] fade-up delay-200">
            Success Is, <span className="italic text-[#D4AF37]">What We Do Best.</span>
          </h1>
          <p className="mt-7 max-w-2xl mx-auto text-[#D8D3C4] text-base sm:text-lg leading-relaxed fade-up delay-300">
            Founded in 1982, GODSC is the premier brotherhood for first-year men at
            Howard University &mdash; <span className="text-white font-medium">open to
            all first-year male students</span> committed to scholarship, leadership,
            and service.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 items-center justify-center fade-up delay-400">
            <Link to="/contact" className="btn-gold" data-testid="hero-cta-interest">
              Prospective Member
            </Link>
            <Link
              to="/events"
              className="btn-outline-navy bg-transparent text-white border-white hover:bg-white hover:text-[#0A1F44]"
              data-testid="hero-cta-events"
            >
              Events
            </Link>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeader
              overline="Who We Are"
              title="The premier male organization at Howard University."
            />
          </div>
          <div className="lg:col-span-7 text-[#24324F] text-[17px] leading-[1.85]">
            <p>
              The Gentlemen of Drew Social Club Incorporated is the largest male
              organization on the campus of Howard University. Founded on
              <em> October 14th, 1982</em>, GODSC brings together first-year men from
              every background &mdash; athletes, scholars, artists, engineers,
              pre-meds, and creatives &mdash; around a shared commitment to
              scholastic success, leadership, and service.
            </p>
            <p className="mt-5">
              We are <strong>open to all first-year male students</strong> at Howard.
              What began in Drew Hall in 1982 continues to evolve, mentoring young
              men, promoting higher education, and carrying forward the legacy of
              the Black leaders and achievers who have paved our way.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-7 text-[#0A1F44] font-medium hover:text-[#C9A227] transition-colors"
              data-testid="home-learn-more"
            >
              <span className="overline">Learn More</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-[#061430] text-white section-pad" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-5">
          <SectionHeader
            overline="Our Three Pillars"
            title="Brotherhood. Scholarship. Service."
            subtitle="Every member commits to these three tenets &mdash; the compass by which we measure our growth and our legacy."
            light
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {PILLARS.map(({ icon: Icon, title, text }, i) => (
              <div
                key={title}
                className="border border-[#1b2a4e] bg-[#0A1F44]/60 p-7 hover:border-[#D4AF37] transition-colors"
                data-testid={`pillar-${i}`}
              >
                <div className="w-11 h-11 flex items-center justify-center border border-[#D4AF37] text-[#D4AF37] mb-5">
                  <Icon size={22} />
                </div>
                <h3 className="font-serif-display text-2xl mb-2">{title}</h3>
                <p
                  className="text-sm text-[#C9C9C9] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEGACY BAND */}
      <section className="section-pad" data-testid="legacy-band">
        <div className="max-w-7xl mx-auto px-5 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 text-center">
          {[
            { k: "1982", v: "Founded" },
            { k: "40+", v: "Years of Excellence" },
            { k: "40", v: "Editions Strong" },
            { k: "#1", v: "Largest Male Org" },
          ].map((s) => (
            <div key={s.v} className="border-t border-[#D4AF37] pt-6">
              <div className="font-serif-display text-5xl text-[#0A1F44]">{s.k}</div>
              <div className="overline text-[#5C5C5C] mt-2">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad" data-testid="cta-section">
        <div className="max-w-6xl mx-auto px-5">
          <div className="border border-[#D4AF37] p-10 md:p-16 bg-[#0A1F44] text-white relative overflow-hidden">
            <div className="grid md:grid-cols-3 gap-10 items-center">
              <div className="md:col-span-2">
                <div className="overline text-[#D4AF37] mb-3">Open to All First-Year Men</div>
                <h3 className="font-serif-display text-4xl sm:text-5xl leading-tight">
                  Interested in joining the brotherhood?
                </h3>
                <p className="mt-5 text-[#D8D3C4] max-w-2xl">
                  If you are a first-year male student at Howard University, we want
                  to hear from you. Submit your interest and begin the conversation.
                </p>
              </div>
              <div className="flex md:justify-end">
                <Link to="/contact" className="btn-gold" data-testid="cta-interest-btn">
                  Begin Your Interest
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
